import React, { createContext, useState, useContext, useEffect } from 'react';
import { setCurrentUser as setMockUser } from '../mock/users';
import api from '../utils/api';
import logger from '../utils/logger';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [weakPasswordInfo, setWeakPasswordInfo] = useState(null); // Info sobre contraseña débil

  // ✅ VERIFICAR SESIÓN DESDE EL SERVIDOR al cargar la aplicación
  useEffect(() => {
    const verifySession = async () => {
      try {
        // Usar el endpoint /me que devuelve el usuario actual autenticado
        const response = await api.get('/usuarios/me');
        
        if (response.data && response.data.user) {
          const userData = response.data.user;
          const mappedUser = {
            id: userData.id,
            name: userData.lastName
              ? `${userData.name} ${userData.lastName}`
              : userData.name,
            lastName: userData.lastName,
            idCard: userData.idCard,
            username: userData.username,
            email: userData.email,
            role: userData.role,
            active: userData.active,
            avatar: userData.avatar,
            officeId: userData.officeId,
            isSuperAdmin: userData.isSuperAdmin || false
          };
          setCurrentUser(mappedUser);
          setMockUser(mappedUser);
        } else {
          setCurrentUser(null);
          setMockUser(null);
        }
      } catch (error) {
        // Si no hay sesión válida (401), el usuario queda en null
        // Esto es normal si no está logueado
        if (error.response?.status !== 401 && error.response?.status !== 403) {
          logger.warn('Error verificando sesión:', error);
        }
        setCurrentUser(null);
        setMockUser(null);
      } finally {
        setLoading(false);
      }
    };

    verifySession();
  }, []);

  const signIn = async ({ email, password }) => {
    try {
      // ✅ Login - El token se guarda automáticamente en cookie httpOnly
      const response = await api.post('/login', { email, password });
      const data = response.data;

      // ✅ YA NO NECESITAMOS VALIDAR TOKEN
      // La cookie httpOnly se gestiona automáticamente
      
      // Mapear el usuario del backend al formato del frontend
      const mappedUser = {
        id: data.user.id,
        name: data.user.last_name
          ? `${data.user.name} ${data.user.last_name}`
          : data.user.name,
        lastName: data.user.last_name,
        idCard: data.user.id_card,
        username: data.user.username,
        email: data.user.email,
        role: data.user.role,
        active: data.user.active,
        avatar: data.user.avatar,
        officeId: data.user.office_id,
        isSuperAdmin: data.user.is_super_admin || false
      };

      setCurrentUser(mappedUser);
      setMockUser(mappedUser); // Keep mock in sync
      
      // ✅ Capturar información sobre fortaleza de contraseña
      if (data.passwordStrength?.isWeak) {
        setWeakPasswordInfo({
          isWeak: true,
          requirements: data.passwordStrength.requirements
        });
      } else {
        setWeakPasswordInfo(null);
      }
      
      return { user: mappedUser, error: null };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Error en el inicio de sesión';
      return { user: null, error: new Error(errorMessage) };
    }
  };

  const login = (user) => {
    setCurrentUser(user);
    setMockUser(user); // Mantenemos el mock sincronizado si se usa en otro lugar
  };

  const logout = async () => {
    try {
      // ✅ Llamar al endpoint de logout para limpiar la cookie
      await api.post('/logout');
    } catch (error) {
      logger.warn('Error al cerrar sesión:', error);
    }
    
    setCurrentUser(null);
    setMockUser(null); // Sincronizamos el mock
    logger.debug('Usuario deslogueado');
  };

  // ✅ YA NO NECESITAMOS VERIFICAR TOKEN MANUALMENTE
  // El servidor valida la cookie automáticamente en cada petición
  const checkTokenValidity = () => {
    // Si hay usuario, asumimos que la cookie es válida
    return !!currentUser;
  };

  const checkTokenRefresh = () => {
    // La cookie se renueva automáticamente
    // No necesitamos verificar manualmente
  };

  const value = { 
    currentUser, 
    setCurrentUser, 
    signIn, 
    login, 
    logout, 
    loading,
    checkTokenValidity,
    checkTokenRefresh,
    weakPasswordInfo, // Info sobre contraseña débil
    dismissWeakPasswordWarning: () => setWeakPasswordInfo(null) // Función para cerrar notificación
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);